import { api } from './api';
import { PricingPlan } from '../types/pricing';

class PricingService {
  private baseUrl = '/v1';

  async getPricingPlans(): Promise<PricingPlan[]> {
    const response = await api.get(`${this.baseUrl}/pricing-plans`);
    return response.data;
  }

  async getPricingPlan(planType: 'essential' | 'gold' | 'premium'): Promise<PricingPlan | null> {
    try {
      const response = await api.get(`${this.baseUrl}/pricing-plans/${planType}`);
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch pricing for ${planType}:`, error);
      return null;
    }
  }

  getPlanPrice(planType: 'essential' | 'gold' | 'premium', plans: PricingPlan[]): number {
    const plan = plans.find(p => p.plan_type === planType && p.is_active);
    return plan ? plan.price : this.getDefaultPrice(planType);
  }

  getPlanFeatures(planType: 'essential' | 'gold' | 'premium', plans: PricingPlan[]): string[] {
    const plan = plans.find(p => p.plan_type === planType && p.is_active);
    return plan ? (plan.features || []) : this.getDefaultFeatures(planType);
  }

  private getDefaultPrice(planType: 'essential' | 'gold' | 'premium'): number {
    const prices = {
      essential: 79900, // $799 in cents
      gold: 199900,     // $1999 in cents
      premium: 399900   // $3999 in cents
    };
    return prices[planType];
  }

  private getDefaultFeatures(planType: 'essential' | 'gold' | 'premium'): string[] {
    const features = {
      essential: [
        'BCP aligned with ISO 22301:2019 (Flash AI)',
        'Incident response checklists & SOPs',
        'Basic customer support'
      ],
      gold: [
        'All Essential Features',
        'Flash RTC out-of-band communication',
        'Dedicated onboarding support'
      ],
      premium: [
        'All Gold Features',
        'Executive leadership training modules',
        'ISO 27001 readiness (cybersecurity)',
        'Customized tabletop scenarios & insurer reports',
        'Resilience Badge (up to 10% insurance discount)',
        'Priority support & quarterly check-ins'
      ]
    };
    return features[planType];
  }

  formatCurrency(amount: number, currency: string = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount / 100);
  }
}

export const pricingService = new PricingService();
